import { DegreeEvent_EventType as DegreeEventType } from '__generated__/graphql-types';
import type {
  DegreeEvent_AcademicEvent as AcademicEvent,
  DegreePlan_AcademicTerm as AcademicTerm,
  DegreePlan_Course as Course,
  DegreePlan_Prerequisite as Prerequisite,
} from '__generated__/graphql-types-do-not-use';

import { getSumOfCredits } from 'bundles/program-loggedin-shared/libraries/gradeUtils';

// #region Terms

export interface LocalAcademicTerm extends Pick<AcademicTerm, 'startDate'> {
  events?: Array<Pick<AcademicEvent, 'eventDate' | 'eventType'>> | null;
}

const getEnrollmentEndDateForTerm = (term: LocalAcademicTerm): Date | null => {
  if (term.events) {
    const enrollmentEndEvent = term.events.find((event) => event.eventType === DegreeEventType.RegistrationEnd);
    if (enrollmentEndEvent) {
      return new Date(enrollmentEndEvent.eventDate);
    }
  }
  return null;
};

const getTermEnrollCloseDate = (term: LocalAcademicTerm): Date => {
  const termEnrollEndDate: Date | null = getEnrollmentEndDateForTerm(term);
  return termEnrollEndDate || new Date(term.startDate);
};

const keepOnlyFutureTerms = <T extends LocalAcademicTerm>(terms: T[]): T[] => {
  const nowDateString = new Date();
  return terms.filter((term) => getTermEnrollCloseDate(term) > nowDateString);
};

const sortTermsByDate = <T extends LocalAcademicTerm>(terms: T[]): T[] =>
  terms.sort(
    (termOne, termTwo) => getTermEnrollCloseDate(termOne).getTime() - getTermEnrollCloseDate(termTwo).getTime()
  );

export const termsForDisplayInCourseContext = <T extends LocalAcademicTerm>(terms: T[]): T[] => {
  const futureTerms = keepOnlyFutureTerms(terms);
  return sortTermsByDate(futureTerms);
};

// #endregion

// #region Elective or Required

interface SegmentForElectiveness {
  completionRule?: {
    minUnits?: number | null;
  } | null;
  courses: Array<{
    segmentCourse: {
      creditValue: number;
    };
  }>;
}

type ElectiveOrRequired = 'elective' | 'required' | 'unknown';
/**
 * Determine whether a segment contains required or elective courses.
 * In the case where a completionRule is missing, return 'unknown'.
 */
export const segmentCoursesAreElectiveOrRequired = (segment: SegmentForElectiveness): ElectiveOrRequired => {
  if (typeof segment.completionRule?.minUnits !== 'number' || segment.courses.length === 0) {
    return 'unknown';
  }
  const totalCourseCredits = getSumOfCredits(segment.courses);
  const allCoursesAreRequired = segment.completionRule.minUnits === totalCourseCredits;
  return allCoursesAreRequired ? 'required' : 'elective';
};

// #endregion

// #region Prerequisites

type EmptyPrerequisiteItem = {};
export type PrerequisiteCourseItem = Pick<Course, 'courseCode'>;
interface PrerequisiteItem extends Pick<Prerequisite, 'completionType'> {
  prerequisiteItems: Array<PrerequisiteCourseItem | EmptyPrerequisiteItem>;
}

export interface TopLevelPrerequisite extends Pick<Prerequisite, 'completionType'> {
  prerequisiteItems: Array<PrerequisiteItem | EmptyPrerequisiteItem>;
}

/**
 * Take a complex prerequisite tree and display it in a joined string.
 *
 * TODO: this needs to be updated to support i18n. See CPM-3098
 */
export const formatPrerequisites = (prerequisite: TopLevelPrerequisite | PrerequisiteItem): string => {
  if (!prerequisite || !prerequisite.prerequisiteItems) return '';

  const innerPrerequisites: string[] = [];

  for (const innerPrerequisite of prerequisite.prerequisiteItems) {
    if ('courseCode' in innerPrerequisite) {
      innerPrerequisites.push((innerPrerequisite as PrerequisiteCourseItem).courseCode);
    } else if ((innerPrerequisite as PrerequisiteItem).completionType === 'ANY') {
      // OR prereqs should be at the start of the list.
      innerPrerequisites.unshift(formatPrerequisites(innerPrerequisite as PrerequisiteItem));
    } else {
      innerPrerequisites.push(formatPrerequisites(innerPrerequisite as PrerequisiteItem));
    }
  }

  let formattedPrerequisite = '';
  if (prerequisite.completionType === 'ALL') {
    formattedPrerequisite = innerPrerequisites.join(', ');
  } else if (prerequisite.completionType === 'ANY') {
    formattedPrerequisite = innerPrerequisites.join(' or ');
  } else {
    formattedPrerequisite = '';
  }

  return formattedPrerequisite;
};

// #endregion
